import CheckIcon from '@mui/icons-material/Check'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { observer } from 'mobx-react-lite'
import { Box, ListItemIcon, MenuItem, Pagination, Typography } from '@mui/material'
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { AppMenu } from 'src/component/common/analog/molecule/list/AppMenu'
import { useAuthStore } from 'src/feature/auth/store/auth.store'
import { OrganisationRole } from 'src/feature/control-panel/model/OrganisationRole'
import { Title1 } from 'src/feature/localization/component/atom/Title1'
import { dayJsInstance } from 'src/feature/localization/model/Time'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { appRoutePath } from 'src/feature/router/util/app-route-path'
import { ColorTokens } from 'src/feature/theme/model/Palette'
import type { TeamOrder, TeamSortBy } from '../../model/my-team'
import { useMyTeamStore } from '../../store/my-team.store'
import { DownloadReport } from './DownloadReport'
import { InsightMetrics } from './InsightMetrics'
import { SkillsSearchBar } from './SkillsSearchBar'
import { TeamListCard } from './TeamListCard'

type FilterOption<T> = { value: T; label: string }

const PAGE_SIZE = 6
const SORT_MENU_ID = 'team-sort-menu'
const DEFAULT_ALPHABETICAL_ORDER: TeamOrder = 'a-z'
const DEFAULT_DATE_ORDER: TeamOrder = 'newest-first'
const CHECK_ICON_STYLES = { color: 'text.primary' }
const METRICS_GRID_STYLES = {
  display: 'grid',
  gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(auto-fill, minmax(220px, 1fr))' },
  gap: 20,
}
const defaultMetricIconProps = {
  iconColor: ColorTokens.primary500,
  iconBackground: ColorTokens.primary10,
}

export const TeamList = observer(() => {
  const { translation } = useLocalizationStore()
  const myTeamStore = useMyTeamStore()
  const navigate = useNavigate()
  const authStore = useAuthStore()
  const { isLoading } = myTeamStore.getGenericFieldsState('getTeamOrganisationOverview')
  const { isLoading: isTeamSkillGapLoading } = myTeamStore.getGenericFieldsState('fetchTeamSkillGapMetricsBatch')

  const translations = translation.pages.myTeam
  const teamOverviewMetricsTranslations = translations.teamOverviewMetrics
  const { userFilter } = translation.pages.controlpanelOrganisation
  const [searchText, setSearchText] = useState('')
  const [skip, setSkip] = useState(0)
  const [showDownloadReport, setShowDownloadReport] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [sortBy, setSortBy] = useState<TeamSortBy>('alphabetical')
  const [order, setOrder] = useState<TeamOrder>(DEFAULT_ALPHABETICAL_ORDER)
  const menuItemRef = useRef<HTMLButtonElement | null>(null)

  const sortOptions: FilterOption<TeamSortBy>[] = [
    { value: 'alphabetical', label: userFilter.sortAlphabetical() },
    { value: 'date-created', label: userFilter.sortDateCreated() },
  ]
  const orderOptions: FilterOption<TeamOrder>[] = sortBy === 'alphabetical'
    ? [
      { value: 'a-z', label: userFilter.orderAlphabeticalAZ() },
      { value: 'z-a', label: userFilter.orderAlphabeticalZA() },
    ]
    : [
      { value: 'newest-first', label: userFilter.orderNewestFirst() },
      { value: 'oldest-first', label: userFilter.orderOldestFirst() },
    ]

  const selectedSortLabel = sortOptions.find(option => option.value === sortBy)?.label || sortOptions[0].label
  const selectedOrderLabel = orderOptions.find(option => option.value === order)?.label || orderOptions[0].label
  const menuLabel = `${selectedSortLabel} - ${selectedOrderLabel}`

  const teamOverview = myTeamStore.teamOrganisationOverview
  const teams = teamOverview.teams
  const teamIds = useMemo(() => teams.map(team => team.id), [teams])
  const teamIdsKey = useMemo(() => teamIds.join('|'), [teamIds])
  const metricsByTeamId = useMemo(() => {
    const lookup: Record<string, typeof myTeamStore.teamSkillGapMetrics[number] | undefined> = {}

    myTeamStore.teamSkillGapMetricLevelIds.forEach((levelId, index) => {
      lookup[levelId] = myTeamStore.teamSkillGapMetrics[index]
    })

    return lookup
  }, [myTeamStore.teamSkillGapMetricLevelIds, myTeamStore.teamSkillGapMetrics])
  const hasTeams = teams.length > 0
  const filteredTeamsCount = teamOverview.filteredTeams ?? teamOverview.totalTeams ?? teams.length
  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredTeamsCount / PAGE_SIZE)), [filteredTeamsCount])
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1
  const visibleStart = hasTeams ? skip + 1 : 0
  const visibleEnd = hasTeams ? Math.min(skip + teams.length, filteredTeamsCount) : 0
  const resultsSummary = translations.resultsSummary(visibleStart, visibleEnd, filteredTeamsCount)
  const totalTeams = teamOverview.totalTeams ?? 0
  const totalUsers = teamOverview.totalUsers ?? 0
  const shouldShowEmptyState = !isLoading && !hasTeams
  const shouldShowTeams = !isLoading && hasTeams

  useEffect(() => {
    myTeamStore.getTeamOrganisationOverview(skip, PAGE_SIZE, searchText, sortBy, order)
  }, [myTeamStore, searchText, skip, sortBy, order])

  useEffect(() => {
    if (!teamIds.length) {
      return
    }

    myTeamStore.fetchTeamSkillGapMetricsBatch(teamIds)
  }, [myTeamStore, teamIds, teamIdsKey])

  const handleSearchChange = useCallback((searchValue: string) => {
    setSearchText(searchValue)
    setSkip(0)
  }, [])

  const handlePageChange = useCallback((_: ChangeEvent<unknown>, page: number) => {
    setSkip((page - 1) * PAGE_SIZE)
  }, [])

  const handleToggleMenu = useCallback(() => {
    setShowMenu(previous => !previous)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setShowMenu(false)
  }, [])

  const handleSortByChange = useCallback((option: TeamSortBy) => {
    setSortBy(option)
    setOrder(option === 'alphabetical' ? DEFAULT_ALPHABETICAL_ORDER : DEFAULT_DATE_ORDER)
    setSkip(0)
    setShowMenu(false)
  }, [])

  const handleOrderChange = useCallback((option: TeamOrder) => {
    setOrder(option)
    setSkip(0)
    setShowMenu(false)
  }, [])

  const reloadTeams = useCallback(async () => {
    await myTeamStore.getTeamOrganisationOverview(skip, PAGE_SIZE, searchText, sortBy, order)

    const reloadedTeamIds = myTeamStore.teamOrganisationOverview.teams.map(team => team.id)

    if (reloadedTeamIds.length > 0) {
      await myTeamStore.fetchTeamSkillGapMetricsBatch(reloadedTeamIds)
    }
  }, [myTeamStore, order, searchText, skip, sortBy])

  const formatHighestRatedSubtitle = (avgSkillRating?: number) => (
    typeof avgSkillRating === 'number'
      ? teamOverviewMetricsTranslations.skillRatingSubtitle(avgSkillRating)
      : teamOverviewMetricsTranslations.noRatingData()
  )

  const formatHighestGapSubtitle = (avgGap?: number, lastUpdatedAt?: string | null) => {
    if (typeof avgGap !== 'number') {
      return teamOverviewMetricsTranslations.noGapData()
    }

    if (lastUpdatedAt) {
      return teamOverviewMetricsTranslations.skillGapReviewedSubtitle(avgGap, dayJsInstance(lastUpdatedAt).fromNow())
    }

    return teamOverviewMetricsTranslations.skillGapSubtitle(avgGap)
  }

  const renderSelectedIcon = (isSelected: boolean) => (
    isSelected ? <CheckIcon fontSize='small' sx={CHECK_ICON_STYLES} /> : null
  )

  return (
    <Box sx={styles.cardWrapper}>
      <Title1 m={0} textValue={translations.title} />
      <Typography variant='body2' sx={styles.subtitle}>
        {translations.subtitle()}
      </Typography>

      <AppBox sx={styles.searchBoxContainer}>
        <AppBox sx={styles.searchBox}>
          <SkillsSearchBar
            count={teamOverview.filteredTeams ?? 0}
            value={searchText}
            onDebouncedChange={handleSearchChange}
            debounceDelay={600}
          />
        </AppBox>
        <AppBox sx={styles.actionsRow}>
          <AppBox
            component='button'
            type='button'
            sx={styles.sortTrigger}
            ref={menuItemRef}
            onClick={handleToggleMenu}
            aria-controls={showMenu ? SORT_MENU_ID : undefined}
            aria-expanded={showMenu ? 'true' : undefined}
            aria-haspopup='menu'
          >
            {menuLabel}
            {showMenu ? <KeyboardArrowUpIcon fontSize='small' sx={{ color: 'inherit' }} /> : <KeyboardArrowDownIcon fontSize='small' sx={{ color: 'inherit' }} />}
          </AppBox>
          <AppMenu
            anchorEl={menuItemRef.current}
            id={SORT_MENU_ID}
            open={showMenu}
            onClose={handleCloseMenu}
          >
            <Typography sx={styles.menuSectionTitle}>{userFilter.sortBy()}</Typography>
            {sortOptions.map(option => (
              <MenuItem key={option.value} onClick={() => handleSortByChange(option.value)} sx={styles.menuItem}>
                <ListItemIcon>{renderSelectedIcon(sortBy === option.value)}</ListItemIcon>
                {option.label}
              </MenuItem>
            ))}
            <Typography sx={styles.menuSectionTitle}>{userFilter.order()}</Typography>
            {orderOptions.map(option => (
              <MenuItem key={option.value} onClick={() => handleOrderChange(option.value)} sx={styles.menuItem}>
                <ListItemIcon>{renderSelectedIcon(order === option.value)}</ListItemIcon>
                {option.label}
              </MenuItem>
            ))}
          </AppMenu>
          <AppButton
            variant='light'
            fontSize={12}
            onClick={() => setShowDownloadReport(true)}
            sx={{ height: 'stretch' }}
          >
            {translations.downloadReport()}
          </AppButton>
        </AppBox>
      </AppBox>

      <InsightMetrics
        sx={METRICS_GRID_STYLES}
        metrics={[
          {
            id: 'total-teams',
            label: teamOverviewMetricsTranslations.totalTeams(),
            value: totalTeams,
            icon: 'BusinessRoundedIcon',
            ...defaultMetricIconProps,
          },
          {
            id: 'total-users',
            label: teamOverviewMetricsTranslations.totalUsers(),
            value: totalUsers,
            icon: 'PeopleRoundedIcon',
            ...defaultMetricIconProps,
          },
          {
            id: 'highest-rated-team',
            label: teamOverviewMetricsTranslations.highestRatedTeam(),
            value: teamOverview.highestAvgRatedTeam?.name || '-',
            subtitle: formatHighestRatedSubtitle(teamOverview.highestAvgRatedTeam?.avgSkillRating),
            toolTipDesc: teamOverviewMetricsTranslations.highestRatedTeamTooltip(),
            icon: 'TrendingUpRoundedIcon',
            ...defaultMetricIconProps,
          },
          {
            id: 'highest-skill-gap-team',
            label: teamOverviewMetricsTranslations.highestSkillGapTeam(),
            value: teamOverview.highestGapTeam?.name || '-',
            subtitle: formatHighestGapSubtitle(teamOverview.highestGapTeam?.avgGap, teamOverview.highestGapTeam?.lastUpdatedAt),
            toolTipDesc: teamOverviewMetricsTranslations.highestSkillGapTeamTooltip(),
            icon: 'ArrowDownwardRoundedIcon',
            iconColor: ColorTokens.red200,
            iconBackground: ColorTokens.red10,
          },
        ]}
        isLoading={isLoading}
      />

      {shouldShowEmptyState && (
        <AppBox sx={styles.emptyStateContainer}>
          <Typography>{translations.managerNoSubteamsMsg()}</Typography>
          {authStore.hasRole(OrganisationRole.SystemAdministrator) && (
            <Typography sx={styles.routeLink} onClick={() => navigate(appRoutePath.controlpanelOrganisation)}>
              {translations.redirectionMessage()}
            </Typography>
          )}
        </AppBox>
      )}

      {shouldShowTeams && (
        <AppBox sx={styles.cardContainer}>
          {teams.map(team => (
            <TeamListCard
              key={team.key}
              levelId={team.id}
              levelName={team.name}
              isMetricsLoading={isTeamSkillGapLoading}
              teamMetrics={metricsByTeamId[team.id]}
              onRoleCreated={reloadTeams}
            />
          ))}
        </AppBox>
      )}

      {shouldShowTeams && (
        <AppBox sx={styles.paginationRow}>
          <Typography sx={styles.paginationSummary}>
            {resultsSummary}
          </Typography>
          <Pagination
            variant='outlined'
            shape='rounded'
            color='secondary'
            count={totalPages}
            page={currentPage}
            showFirstButton
            showLastButton
            onChange={handlePageChange}
          />
        </AppBox>
      )}
      {showDownloadReport && (
        <DownloadReport
          show={showDownloadReport}
          onClose={() => setShowDownloadReport(false)}
        />
      )}
    </Box>
  )
})

const styles = {
  cardWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    minHeight: 'calc(100vh - 100px)',
  },
  subtitle: {
    color: 'text.secondary',
    marginTop: '-15px',
  },
  paginationRow: {
    width: '100%',
    flexShrink: 0,
    display: 'flex',
    justifyContent: { xs: 'center', md: 'space-between' },
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    backgroundColor: 'background.dark',
    padding: '10px 20px',
    border: '1px solid',
    borderColor: 'background.default',
    borderRadius: '10px',
  },
  paginationSummary: {
    color: 'text.secondary',
    fontSize: 12,
    width: { xs: '100%', md: 'auto' },
    textAlign: { xs: 'center', md: 'left' },
  },
  searchBoxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  actionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },
  sortTrigger: {
    display: 'flex',
    flexDirection: 'row',
    gap: 1,
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: 12,
    border: '1px solid',
    borderColor: 'primary.main',
    backgroundColor: 'primary.main',
    color: 'background.default',
    p: '5px 10px',
    borderRadius: '10px',
  },
  menuSectionTitle: {
    color: 'text.secondary',
    padding: '0.5rem 1rem',
    fontSize: 12,
  },
  menuItem: {
    fontSize: 12,
  },
  searchBox: {
    width: { xs: '100%', md: '30em' },
  },
  emptyStateContainer: {
    color: 'text.secondary',
    gap: 5,
  },
  cardContainer: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fill, minmax(350px, 1fr))' },
    width: '100%',
    gap: 20,
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    alignContent: 'start',
  },
  routeLink: {
    color: 'secondary.main',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}
